import { Progress } from '@bpinternal/ui-kit-next'
import { useNavigate, useParams } from '@tanstack/react-router'
import Lottie from 'lottie-react'
import { type FC, useCallback, useEffect } from 'react'
import coffeeAnimationDark from '~/assets/lottie-animations/coffee-animation-dark.json'
import coffeeAnimationLight from '~/assets/lottie-animations/coffee-animation-light.json'
import { trackEvent } from '~/providers'
import { useLocalStore } from '~/stores'
import { getUseBotOnboardingStore, IDENTITY_WORKFLOW_CHILD_DESCRIPTION } from '../../stores/useBotOnboardingStore'
import { AnimatedFilesList } from './AnimatedFilesList'
import { StepCardContent } from './StepCard'

type DeploymentStepProps = {
  className?: string
}

export const DeploymentStep: FC<DeploymentStepProps> = () => {
  const navigate = useNavigate()
  const params = useParams({ from: '/workspaces/$workspaceId/bots/$botId/new-bot' })
  const { workspaceId, botId } = params

  const isDarkTheme = useLocalStore((state) => state.getPreferredTheme() === 'dark')

  const useBotOnboardingStore = getUseBotOnboardingStore({ botId, workspaceId })
  const identityStatus = useBotOnboardingStore((state) => state.identityStatus)
  const deploymentProgress = useBotOnboardingStore((state) => state.deploymentProgress)

  const navigateToNewChat = useCallback(() => {
    trackEvent({ type: 'onboarding_splash_screen_shown', botId, workspaceId })
    navigate({
      to: '/workspaces/$workspaceId/bots/$botId/new-chat',
      params: { workspaceId, botId },
    })
  }, [workspaceId, botId, navigate])

  useEffect(() => {
    if (identityStatus === 'completed') {
      navigateToNewChat()
    }
  }, [identityStatus, navigateToNewChat])

  return (
    <StepCardContent
      title="Setup complete"
      description="Personalizing your agent with your content."
      className="flex flex-col gap-10"
    >
      <div className="mt-2 flex flex-col gap-2">
        <p className="text-sm">
          {deploymentProgress.stepDescription ?? IDENTITY_WORKFLOW_CHILD_DESCRIPTION[0]}
          <span className="animate-caret-blink">.</span>
          <span className="animate-caret-blink [animation-delay:0.2s]">.</span>
          <span className="animate-caret-blink [animation-delay:0.4s]">.</span>
        </p>
        <Progress value={deploymentProgress.progress} />
      </div>
      <AnimatedFilesList workspaceId={workspaceId} botId={botId} />
      <div className="flex flex-col items-center gap-2">
        <Lottie
          animationData={isDarkTheme ? coffeeAnimationDark : coffeeAnimationLight}
          loop={true}
          style={{ width: 50, height: 50 }}
        />
        <p className="font-medium">This may take some time</p>
        <p className="text-center">Sit back and relax while we set your bot up for you.</p>
      </div>
    </StepCardContent>
  )
}
